/**
 * useMediaQuery — reactive matchMedia hook.
 *
 * Used by the dashboard background system to detect `prefers-reduced-motion`
 * and small screens, so the animated Remotion background can degrade to a
 * static image without penalizing performance (issue #414).
 *
 * @module hooks/useMediaQuery
 */

import { useEffect, useState } from 'react';

const getInitialMatch = (query) => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(query).matches;
};

export const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => getInitialMatch(query));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mql = window.matchMedia(query);
    const handleChange = (event) => setMatches(event.matches);

    // Sync in case the query string changed between renders.
    setMatches(mql.matches);

    if (mql.addEventListener) {
      mql.addEventListener('change', handleChange);
      return () => mql.removeEventListener('change', handleChange);
    }
    // Safari < 14 fallback
    mql.addListener(handleChange);
    return () => mql.removeListener(handleChange);
  }, [query]);

  return matches;
};

export default useMediaQuery;
